<template>
    <div>
        <!-- Drop / click zone -->
        <div
            v-if="!value"
            class="slip-upload-zone"
            :class="{ 'is-dragging': isDragging, 'is-loading': loading }"
            @click="!loading && $refs.fileInput.click()"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
        >
            <transition name="fade" mode="out-in">
                <div v-if="loading" key="loading" class="slip-upload-inner">
                    <i class="fas fa-circle-notch fa-spin tw-text-blue-400 tw-text-2xl"></i>
                    <span class="tw-text-sm tw-text-gray-500 tw-mt-2">Uploading…</span>
                </div>
                <div v-else key="idle" class="slip-upload-inner">
                    <div class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-mb-2">
                        <i class="fas fa-cloud-upload-alt tw-text-blue-400 tw-text-xl"></i>
                    </div>
                    <span class="tw-text-sm tw-font-medium tw-text-gray-600">
                        {{ isDragging ? $t('dropHere') : $t('clickOrDragToUpload') }}
                    </span>
                    <span class="tw-text-xs tw-text-gray-400 tw-mt-0.5">JPG, PNG, PDF</span>
                </div>
            </transition>
            <input
                ref="fileInput"
                type="file"
                accept="image/*,application/pdf"
                class="tw-hidden"
                @change="onFileChange"
            />
        </div>

        <!-- Preview after upload -->
        <div v-else class="slip-preview">
            <div class="slip-preview-inner">
                <!-- Image thumbnail -->
                <div class="tw-flex-shrink-0">
                    <img
                        v-if="previewSrc && isImage"
                        :src="previewSrc"
                        class="tw-w-14 tw-h-14 tw-rounded-lg tw-object-cover tw-border tw-border-green-200"
                    />
                    <div
                        v-else
                        class="tw-w-14 tw-h-14 tw-rounded-lg tw-bg-green-50 tw-border tw-border-green-200 tw-flex tw-items-center tw-justify-center"
                    >
                        <i class="fas fa-file-alt tw-text-green-400 tw-text-2xl"></i>
                    </div>
                </div>

                <div class="tw-flex-1 tw-min-w-0">
                    <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-0.5">
                        <i class="fas fa-check-circle tw-text-green-500 tw-text-sm"></i>
                        <span class="tw-text-xs tw-font-semibold tw-text-green-700">Uploaded</span>
                    </div>
                    <p class="tw-text-xs tw-text-gray-600 tw-truncate tw-mb-0" :title="fileName">{{ fileName }}</p>
                </div>

                <button
                    type="button"
                    class="tw-flex-shrink-0 tw-w-6 tw-h-6 tw-rounded-full tw-bg-gray-100 hover:tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-transition-colors"
                    :title="$t('remove')"
                    @click="onClear"
                >
                    <i class="fas fa-times tw-text-gray-400 hover:tw-text-red-500 tw-text-xs"></i>
                </button>
            </div>

            <!-- Change link -->
            <button
                type="button"
                class="tw-mt-1.5 tw-text-xs tw-text-blue-500 hover:tw-underline tw-block tw-w-full tw-text-center"
                @click="$refs.changeInput.click()"
            >
                <i class="fas fa-sync-alt tw-text-xs tw-mr-1"></i>{{ $t('changeFile') }}
            </button>
            <input
                ref="changeInput"
                type="file"
                accept="image/*,application/pdf"
                class="tw-hidden"
                @change="onFileChange"
            />
        </div>

        <!-- Error -->
        <div v-if="error" class="invalid-feedback d-block tw-mt-1">{{ error }}</div>
    </div>
</template>

<script>
export default {
    name: "BankSlipPhotoUpload",
    props: {
        value:   { type: String, default: null },
        loading: { type: Boolean, default: false },
        error:   { type: String, default: null }
    },
    data() {
        return {
            isDragging: false,
            localPreview: null
        };
    },
    computed: {
        fileName() {
            if (!this.value) return "";
            return this.value.split("/").pop();
        },
        isImage() {
            return /\.(jpe?g|png|gif|webp)$/i.test(this.fileName);
        },
        previewSrc() {
            return this.localPreview || this.value || null;
        }
    },
    watch: {
        value(val) {
            if (!val) this.localPreview = null;
        }
    },
    methods: {
        onFileChange(e) {
            const file = e.target.files[0];
            if (!file) return;
            if (file.type.startsWith("image/")) {
                const reader = new FileReader();
                reader.onload = ev => { this.localPreview = ev.target.result; };
                reader.readAsDataURL(file);
            }
            this.$emit("change", file);
            e.target.value = "";
        },
        onDrop(e) {
            this.isDragging = false;
            const file = e.dataTransfer.files[0];
            if (!file) return;
            if (file.type.startsWith("image/")) {
                const reader = new FileReader();
                reader.onload = ev => { this.localPreview = ev.target.result; };
                reader.readAsDataURL(file);
            }
            this.$emit("change", file);
        },
        onClear() {
            this.localPreview = null;
            this.$emit("clear");
        }
    }
};
</script>

<style scoped>
.slip-upload-zone {
    border: 2px dashed #d1d5db;
    border-radius: 10px;
    padding: 20px 16px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    background: #fafafa;
    text-align: center;
}
.slip-upload-zone:hover,
.slip-upload-zone.is-dragging {
    border-color: #3b82f6;
    background: #eff6ff;
}
.slip-upload-zone.is-loading {
    cursor: default;
    opacity: 0.8;
}
.slip-upload-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.slip-preview {
    border-radius: 10px;
    border: 1px solid #bbf7d0;
    background: #f0fdf4;
    padding: 10px 12px;
}
.slip-preview-inner {
    display: flex;
    align-items: center;
    gap: 10px;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.15s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
